/**
 * Traspaso de derechos: la casa cambia de titular (recorrido P3).
 *
 * Herencia, venta o cambio de titular son operaciones mensuales del comité.
 * Antes había que finalizar el contrato y crear otro a mano, con la deuda del
 * anterior colgando del socio viejo.
 *
 * Las dos decisiones las toma la administradora acá:
 *   1. qué pasa con la deuda **de esa casa** (no con todo lo que deba la
 *      persona: puede tener otro arranque);
 *   2. si el titular nuevo queda socio o cliente.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ArrowLeftRight } from "lucide-react";

import { ApiError } from "../../lib/api";
import { useToast } from "../../components/Toast";
import { Modal } from "../../components/Modal";
import {
  Button,
  Cargando,
  Input,
  Select,
  Textarea,
  useDescarteProtegido,
  useHayCambios,
} from "../../components/ui";
import { listarSocios } from "../socios/api";
import { fetchEmpresaConfiguracion } from "../empresa/api";
import { hoyLocalISO } from "../../lib/fechaLocal";
import {
  deudaDelContrato,
  traspasarContrato,
  type MotivoTraspaso,
  type TratamientoDeuda,
} from "./api";

const MOTIVOS: { value: MotivoTraspaso; label: string; ayuda: string }[] = [
  {
    value: "Herencia",
    label: "Herencia",
    ayuda: "El heredero sucede a quien ya era socio: no paga cuota de incorporación.",
  },
  {
    value: "Venta",
    label: "Venta del derecho",
    ayuda: "Si quiere ser socio, se incorpora y paga la cuota.",
  },
  {
    value: "Otro",
    label: "Otro cambio de titular",
    ayuda: "Si quiere ser socio, se incorpora y paga la cuota.",
  },
];

const TRATAMIENTOS: {
  value: TratamientoDeuda;
  label: string;
  ayuda: string;
}[] = [
  {
    value: "bloquear",
    label: "No traspasar hasta que se pague",
    ayuda: "El trámite queda en suspenso. Nada cambia hasta que salde.",
  },
  {
    value: "traspasar",
    label: "Traspasar la deuda al titular nuevo",
    ayuda: "Se descarga del anterior y se le cobra al nuevo en su primera boleta.",
  },
  {
    value: "condonar",
    label: "Condonar la deuda",
    ayuda: "El comité la perdona: no se la cobra a nadie.",
  },
  {
    value: "mantener",
    label: "Dejarla en el titular anterior",
    ayuda: "El comité se la sigue cobrando a él, por fuera de esta casa.",
  },
];

function pesos(n: number): string {
  return `$${n.toLocaleString("es-CL")}`;
}

export function TraspasoModal({
  contratoId,
  socioActualId,
  direccion,
  onClose,
}: {
  contratoId: number;
  socioActualId: number;
  direccion: string;
  onClose: () => void;
}): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();

  const [nuevoSocioId, setNuevoSocioId] = useState("");
  const [motivo, setMotivo] = useState<MotivoTraspaso>("Venta");
  const [tratamiento, setTratamiento] = useState<TratamientoDeuda>("mantener");
  const [quiereSerSocio, setQuiereSerSocio] = useState(false);
  const [fecha, setFecha] = useState(hoyLocalISO());
  const [detalle, setDetalle] = useState("");
  const [error, setError] = useState<string | null>(null);

  const deudaQ = useQuery({
    queryKey: ["contrato", contratoId, "deuda"],
    queryFn: () => deudaDelContrato(contratoId),
  });
  const sociosQ = useQuery({
    queryKey: ["socios", { only_active: true, only_socios: false }],
    queryFn: () => listarSocios({ only_active: true, only_socios: false }),
  });
  const cfgQ = useQuery({
    queryKey: ["empresa", "configuracion"],
    queryFn: fetchEmpresaConfiguracion,
  });

  const deuda = deudaQ.data?.deuda ?? 0;
  const cuota = cfgQ.data?.cuota_incorporacion ?? 0;

  // El titular actual no puede recibirse a sí mismo; los fallecidos tampoco
  // pueden tomar un derecho (el backend lo rechaza igual).
  const candidatos = useMemo(
    () =>
      (sociosQ.data ?? []).filter(
        (s) => s.id !== socioActualId && s.estado !== "Fallecido",
      ),
    [sociosQ.data, socioActualId],
  );
  const nuevoSocio = candidatos.find((s) => String(s.id) === nuevoSocioId);
  const yaEraSocio = nuevoSocio?.es_socio ?? false;
  const cobraCuota =
    quiereSerSocio && !yaEraSocio && motivo !== "Herencia" && cuota > 0;

  const mut = useMutation({
    mutationFn: () =>
      traspasarContrato(contratoId, {
        nuevo_socio_id: Number(nuevoSocioId),
        motivo,
        tratamiento_deuda: tratamiento,
        nuevo_titular_socio: quiereSerSocio,
        fecha: fecha || null,
        detalle: detalle.trim() || null,
      }),
    onSuccess: (r) => {
      toast.success(
        `Derecho traspasado · contrato #${r.contrato_nuevo_id}` +
          (r.cuota_incorporacion > 0
            ? ` · cuota de ${pesos(r.cuota_incorporacion)} cargada`
            : ""),
      );
      void qc.invalidateQueries({ queryKey: ["contratos"] });
      void qc.invalidateQueries({ queryKey: ["contrato", contratoId] });
      void qc.invalidateQueries({ queryKey: ["socios"] });
      onClose();
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError ? err.message : "No se pudo traspasar";
      setError(msg);
      toast.error(msg);
    },
  });

  const motivoElegido = MOTIVOS.find((m) => m.value === motivo);

  // Cambios sin guardar: cerrar pregunta antes de descartar (auditoría UX-01).
  const hayCambios = useHayCambios(
    [
      nuevoSocioId, motivo, tratamiento, quiereSerSocio, fecha, detalle,
    ],
  );
  const { cerrar: intentarCerrar, dialogo: dialogoDescarte } = useDescarteProtegido(
    hayCambios,
    onClose,
    mut.isPending,
  );

  return (
    <Modal
      open
      onClose={intentarCerrar}
      title="Traspasar el derecho"
      subtitle={direccion}
      icon={ArrowLeftRight}
      size="lg"
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={intentarCerrar} disabled={mut.isPending}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setError(null);
              if (!nuevoSocioId) {
                setError("Elige quién recibe el derecho");
                return;
              }
              mut.mutate();
            }}
            disabled={mut.isPending}
          >
            {mut.isPending ? "Traspasando…" : "Traspasar"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-sm text-neutral-600">
          Se finaliza el contrato actual y se abre uno nuevo sobre el mismo
          arranque, con la misma tarifa y el mismo medidor.
        </p>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Select
            label="Nuevo titular"
            required
            value={nuevoSocioId}
            onChange={(e) => setNuevoSocioId(e.target.value)}
            disabled={mut.isPending}
          >
            <option value="">— elegir —</option>
            {candidatos.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nombre} · {s.rut}
                {s.es_socio ? " · socio" : ""}
              </option>
            ))}
          </Select>
          <Select
            label="Motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value as MotivoTraspaso)}
            disabled={mut.isPending}
          >
            {MOTIVOS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </Select>
        </div>
        {motivoElegido && (
          <p className="-mt-2 text-xs text-neutral-500">{motivoElegido.ayuda}</p>
        )}

        {/* ── Deuda de la casa ───────────────────────────────────────── */}
        <section className="rounded-lg border border-neutral-200 p-3">
          <h4 className="text-sm font-medium text-neutral-900">
            Deuda de esta casa
          </h4>
          {deudaQ.isPending ? (
            <Cargando inline texto="Consultando…" className="mt-1" />
          ) : deuda === 0 ? (
            <p className="mt-1 text-sm text-emerald-700">
              Sin boletas impagas. No hay nada que decidir.
            </p>
          ) : (
            <>
              <p className="mt-1 flex items-center gap-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4" strokeWidth={1.5} />
                <span>
                  El titular anterior debe <strong>{pesos(deuda)}</strong> de
                  este arranque. Es sólo lo de esta casa: si tiene otro
                  arranque, esa deuda no se mueve.
                </span>
              </p>
              <div className="mt-2 space-y-1.5">
                {TRATAMIENTOS.map((t) => (
                  <label
                    key={t.value}
                    className="flex cursor-pointer items-start gap-2 rounded-md p-1.5 hover:bg-neutral-50"
                  >
                    <input
                      type="radio"
                      name="tratamiento-deuda"
                      className="mt-0.5"
                      checked={tratamiento === t.value}
                      onChange={() => setTratamiento(t.value)}
                      disabled={mut.isPending}
                    />
                    <span className="text-sm">
                      <span className="text-neutral-800">{t.label}</span>
                      <span className="block text-xs text-neutral-500">
                        {t.ayuda}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            </>
          )}
        </section>

        {/* ── Socio o cliente ────────────────────────────────────────── */}
        <section className="rounded-lg border border-neutral-200 p-3">
          <h4 className="text-sm font-medium text-neutral-900">
            ¿El titular nuevo queda como socio?
          </h4>
          <label className="mt-2 flex cursor-pointer items-start gap-2">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={quiereSerSocio}
              onChange={(e) => setQuiereSerSocio(e.target.checked)}
              disabled={mut.isPending}
            />
            <span className="text-sm text-neutral-800">
              Sí, quiere el derecho de socio
              <span className="block text-xs text-neutral-500">
                La incorporación es voluntaria y personal. Si no la toma, queda
                como cliente: mismo servicio, pero su boleta lleva IVA.
              </span>
            </span>
          </label>
          {quiereSerSocio && (
            <p className="mt-2 text-xs text-neutral-600">
              {yaEraSocio
                ? "Ya es socio del comité: no paga la cuota de nuevo."
                : motivo === "Herencia"
                  ? "Por herencia continúa como socio sin pagar cuota de incorporación."
                  : cuota > 0
                    ? `Se le cargará la cuota de incorporación de ${pesos(cuota)}, que se cobra en su próxima boleta (exenta de IVA).`
                    : "El comité no tiene cuota de incorporación configurada, así que no se cobra nada. Se define en Configuración · Boleta."}
            </p>
          )}
          {cobraCuota && (
            <p className="mt-1 text-xs text-neutral-500">
              Queda socio desde ya: el estatuto tipo reconoce la calidad de
              socio a quien pagó <em>o se encuentra pagando</em> la cuota.
            </p>
          )}
        </section>

        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <Input
            label="Fecha del traspaso"
            type="date"
            value={fecha}
            onChange={(e) => setFecha(e.target.value)}
            disabled={mut.isPending}
          />
        </div>
        <Textarea
          label="Detalle (queda en la bitácora)"
          rows={2}
          placeholder="N° de escritura, acuerdo del directorio, etc."
          value={detalle}
          onChange={(e) => setDetalle(e.target.value)}
          disabled={mut.isPending}
        />

        {error && <p className="text-sm text-rose-700">{error}</p>}
      </div>
      {dialogoDescarte}
    </Modal>
  );
}
